// Import React and hooks for component logic
import React, { useEffect, useRef, useState } from "react";
// Import the CSS file for custom select styling
import "../../css/CustomSelect.css";
// Import the arrow down SVG icon as a React component
import { ReactComponent as ArrowDown } from "../../assets/icons/arrow_down.svg";

// CustomSelect is a reusable dropdown select component
// Props:
// - options: Array of { value, label } objects for dropdown options
// - value: Currently selected value
// - onChange: Callback when selection changes
// - name: Name attribute for the select
// - placeholder: Placeholder text when no option is selected
// - className: Additional CSS classes
export default function CustomSelect({
  options = [],
  value = "",
  onChange,
  name = "",
  placeholder = "Please select",
  className = "",
}) {
  // State to control dropdown open/close
  const [isOpen, setIsOpen] = useState(false);
  // Ref for detecting clicks outside the dropdown
  const wrapperRef = useRef(null);

  // Find the currently selected option object
  const selectedOption = options.find((option) => option.value === value);

  // Effect to close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle selecting an option
  const handleSelect = (optionValue) => {
    if (onChange) {
      onChange({
        target: {
          name,
          value: optionValue,
        },
      });
    }
    setIsOpen(false);
  };

  return (
    // Wrapper div for the custom select dropdown
    <div ref={wrapperRef} className={`custom-select ${className}`}>
      {/* Button to trigger dropdown open/close */}
      <button
        type="button"
        className={`custom-select-trigger ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {/* Display selected option or placeholder */}
        <span className="custom-select-text">
          {selectedOption?.label || placeholder}
        </span>
        {/* Dropdown arrow icon */}
        <span className={`custom-select-arrow ${isOpen ? "open" : ""}`}>
          <ArrowDown />
        </span>
      </button>

      {/* Dropdown options list */}
      {isOpen && (
        <div className="custom-select-dropdown">
          {options.map((option) => {
            const isSelected = option.value === value;

            return (
              // Option button for each dropdown item
              <button
                key={option.value || option.label}
                type="button"
                className={`custom-select-option ${
                  isSelected ? "selected" : ""
                }`}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
